/* Progressive enhancement only. Article content and social links are server-rendered. */
if (!customElements.get('water-article')) {
  customElements.define('water-article', class WaterArticle extends HTMLElement {
    connectedCallback() {
      // A deferred script normally sees all children. Also handle streamed/editor insertion.
      if (this.querySelector('[data-article-body]')) this.initialize();
      else {
        this.mountObserver = new MutationObserver(() => {
          if (!this.querySelector('[data-article-body]')) return;
          this.mountObserver.disconnect();
          this.initialize();
        });
        this.mountObserver.observe(this, { childList: true, subtree: true });
      }
    }

    initialize() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.body = this.querySelector('[data-article-body]');
      this.progress = this.querySelector('[data-progress]');
      this.links = [];
      this.headings = [];
      this.frame = 0;
      this.activeIndex = -1;
      this.buildContents();
      this.prepareTables();
      this.updateReadingTime();
      this.positionTiein();

      this.copyButton = this.querySelector('[data-copy-link]');
      if (this.copyButton) {
        this.copyButton.hidden = false;
        this.copyButton.addEventListener('click', () => this.copyLink(), { signal });
      }
      if (this.progress) this.progress.hidden = false;
      if (this.progress || this.headings.length) {
        window.addEventListener('scroll', () => this.schedule(), { signal, passive: true });
        window.addEventListener('resize', () => this.measure(), { signal, passive: true });
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this);
        const siteHeader = document.getElementById('header-component');
        if (siteHeader) this.resizeObserver.observe(siteHeader);
        this.measure();
      }
      // Restore deep links to generated heading IDs, which don't exist before enhancement.
      if (window.location.hash) {
        let id;
        try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
        const target = this.headings.find(heading => heading.id === id);
        if (target?.dataset.waGeneratedId) target.scrollIntoView({ block: 'start' });
      }
    }

    buildContents() {
      const list = this.querySelector('[data-toc-list]');
      if (!list) return;
      const selector = this.dataset.tocLevels === 'h2,h3' ? 'h2,h3' : 'h2';
      this.headings = [...this.body.querySelectorAll(selector)].filter(heading => heading.textContent.trim() && !heading.closest('[data-article-tiein]'));
      if (!this.headings.length) return;
      const ids = new Set([...document.querySelectorAll('[id]')].map(element => element.id));
      const fragment = document.createDocumentFragment();
      this.headings.forEach((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const slug = heading.textContent.trim().toLowerCase().normalize('NFKC')
            .replace(/[^\p{L}\p{N}\s-]/gu, '').replace(/[\s-]+/g, '-').replace(/^-|-$/g, '') || `section-${index + 1}`;
          let id = slug;
          let suffix = 2;
          while (ids.has(id)) id = `${slug}-${suffix++}`;
          heading.id = id;
          heading.dataset.waGeneratedId = 'true';
          ids.add(id);
        }
        const item = document.createElement('li');
        if (heading.tagName === 'H3') item.className = 'wa-toc-sub';
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        fragment.append(item);
        this.links.push(link);
        link.addEventListener('click', event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          // Native URL fragments preserve back/forward navigation without scroll listeners on every link.
          history.pushState(null, '', link.getAttribute('href'));
          const details = this.querySelector('.wa-toc');
          if (this.mobile.matches) details.open = false;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          heading.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }, { signal: this.controller.signal });
      });
      list.replaceChildren(fragment);
      this.querySelector('[data-article-aside]').hidden = false;
      this.querySelector('.wa-toc').hidden = false;
      this.querySelector('[data-article-layout]').classList.add('wa-layout--with-toc');
      this.mobile = window.matchMedia('(max-width: 900px)');
      const adaptContents = () => { this.querySelector('.wa-toc').open = !this.mobile.matches; };
      adaptContents();
      this.mobile.addEventListener('change', adaptContents, { signal: this.controller.signal });
    }

    positionTiein() {
      const card = this.querySelector('[data-article-tiein]');
      const target = card?.dataset.beforeHeading.trim();
      if (!target) return; // Server-rendered fallback stays after the article body.
      const normalize = text => text.trim().normalize('NFKC').replace(/\s+/g, ' ').toLocaleLowerCase();
      const heading = [...this.body.querySelectorAll('h2,h3')].find(node =>
        !node.closest('[data-article-tiein]') && (node.id === target.replace(/^#/, '') || normalize(node.textContent) === normalize(target))
      );
      // Move the same server-rendered node; never rewrite or split article HTML.
      if (heading) heading.before(card);
    }

    prepareTables() {
      this.body.querySelectorAll('table').forEach(table => {
        let wrapper = table.parentElement;
        if (!wrapper.classList.contains('wa-table-scroll')) {
          wrapper = document.createElement('div');
          wrapper.className = 'wa-table-scroll';
          table.before(wrapper);
          wrapper.append(table);
        }
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || this.dataset.tableLabel);
      });
    }

    updateReadingTime() {
      const estimate = this.querySelector('[data-reading-time]');
      if (!estimate) return;
      const content = this.body.cloneNode(true);
      content.querySelectorAll('[data-article-tiein]').forEach(node => node.remove());
      const text = content.textContent.trim();
      let words;
      if (typeof Intl.Segmenter === 'function') {
        const segments = new Intl.Segmenter(document.documentElement.lang || undefined, { granularity: 'word' });
        words = [...segments.segment(text)].filter(segment => segment.isWordLike).length;
      } else words = text.split(/\s+/u).filter(Boolean).length;
      const speed = Math.max(1, Number(this.dataset.readingSpeed) || 220);
      estimate.textContent = String(Math.max(1, Math.ceil(words / speed)));
    }

    measure() {
      const scrollY = window.scrollY;
      const bounds = this.body.getBoundingClientRect();
      this.bodyStart = bounds.top + scrollY;
      this.bodyHeight = bounds.height;
      this.headingPositions = this.headings.map(heading => heading.getBoundingClientRect().top + scrollY);
      this.stickyOffset = parseFloat(getComputedStyle(this).getPropertyValue('--header-height')) || 80;
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.isConnected) return;
        const availableHeight = Math.max(1, window.innerHeight - this.stickyOffset - 24);
        const start = this.bodyStart - this.stickyOffset - 24;
        const distance = this.bodyHeight - availableHeight;
        const progress = distance > 0 ? Math.min(1, Math.max(0, (window.scrollY - start) / distance)) : Number(window.scrollY + window.innerHeight >= this.bodyStart + this.bodyHeight);
        if (this.progress) this.progress.style.transform = `scaleX(${progress})`;
        if (!this.headings.length) return;
        let active = 0;
        for (let index = 0; index < this.headingPositions.length; index++) {
          if (this.headingPositions[index] <= window.scrollY + this.stickyOffset + 40) active = index;
          else break;
        }
        if (active === this.activeIndex) return;
        this.links[this.activeIndex]?.removeAttribute('aria-current');
        this.links[active].setAttribute('aria-current', 'location');
        this.activeIndex = active;
      });
    }

    async copyLink() {
      const status = this.querySelector('[data-copy-status]');
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(this.dataset.shareUrl);
        else {
          const field = document.createElement('textarea');
          field.className = 'wa-sr-only';
          field.value = this.dataset.shareUrl;
          this.append(field);
          field.select();
          let copied;
          try { copied = document.execCommand('copy'); } finally { field.remove(); this.copyButton.focus(); }
          if (!copied) throw new Error('Clipboard unavailable');
        }
        if (!this.isConnected) return;
        status.textContent = this.dataset.copiedLabel;
        this.originalCopyLabel ??= this.copyButton.textContent;
        this.copyButton.textContent = this.dataset.copiedLabel;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          this.copyButton.textContent = this.originalCopyLabel;
          status.textContent = '';
        }, 2500);
      } catch {
        if (this.isConnected) status.textContent = this.dataset.copyError;
      }
    }

    disconnectedCallback() {
      this.mountObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.copyTimer);
      if (this.copyButton && this.originalCopyLabel != null) this.copyButton.textContent = this.originalCopyLabel;
      const status = this.querySelector('[data-copy-status]');
      if (status) status.textContent = '';
    }
  });
}
